import { Icon } from "@circle-react-shared/Icon";
import type { IconType } from "@circle-react-shared/Icon";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import isFunction from "lodash/isFunction";
import { useMemo } from "react";

import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import type { ButtonVariant } from "@/react/components/SettingsApp/CompassCompat/Button";

export interface Option {
  icon?: IconType;
  label: string;
  onClick?: (...args: any[]) => void;
  value: string;
}

const DEFAULT_MENU_ITEMS_CLASS_NAME =
  "!w-max [&_button]:whitespace-nowrap [&_a]:whitespace-nowrap";

export interface ActionSelectorButtonProps {
  disabled: boolean;
  large?: boolean;
  menuItemsClassName?: string;
  onChange?: (action?: Option) => void;
  options: Option[];
  selectedAction?: Option;
  setSelectedAction: (action?: Option) => void;
  type: "button" | "submit" | "reset";
  variant?: ButtonVariant;
}

export const ActionSelectorButton = ({
  variant,
  options,
  selectedAction,
  setSelectedAction,
  onChange,
  disabled,
  type = "button",
  large = false,
  menuItemsClassName = DEFAULT_MENU_ITEMS_CLASS_NAME,
}: ActionSelectorButtonProps) => {
  const selectedOption =
    options.find(opt => opt.value === selectedAction?.value) ?? options[0];

  const optionsWithoutSelected = useMemo(
    () => options.filter(opt => opt.value !== selectedOption.value),
    [options, selectedOption],
  );

  const hasSecondaryOptions = optionsWithoutSelected.length > 0;

  return (
    <div
      className={classNames(
        "flex items-stretch",
        hasSecondaryOptions && "gap-[1px]",
      )}
    >
      <Button
        type={type}
        variant={variant}
        className={classNames("truncate", {
          "rounded-l-full rounded-r-none": hasSecondaryOptions,
          "rounded-full": !hasSecondaryOptions,
        })}
        disabled={disabled}
        data-testid="perform-action-button"
        large={large}
        tabIndex={0}
        onClick={() => {
          if (isFunction(selectedOption.onClick)) {
            selectedOption.onClick();
          }
        }}
      >
        {selectedOption.label}
      </Button>
      {hasSecondaryOptions && (
        <Dropdown
          direction="top-end"
          buttonType="button"
          linkType="button"
          buttonWrapperClassName={classNames("flex", {
            "pointer-events-none": disabled,
          })}
          menuItemsClassName={menuItemsClassName}
          popperOptions={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 8],
                },
              },
            ],
          }}
          button={
            <Button
              type="button"
              variant={variant}
              className="rounded-l-none rounded-r-full !px-2.5"
              data-testid="select-action-button"
              disabled={disabled}
              large={large}
            >
              <Icon type="16-chevron-down" size={16} className="shrink-0" />
            </Button>
          }
          options={optionsWithoutSelected}
          onChange={value => {
            const action = options.find(option => option.value === value);
            setSelectedAction(action);
            if (isFunction(onChange)) onChange(action);
          }}
        />
      )}
    </div>
  );
};
